import { Column, ColumnHeader, ColumnContent } from '../../shared/Column'
import styles from './overviewStyles.module.scss'
import { SavedFitBoxContent } from '@components/collection/CollectionFilter/FiltersSidePanel/SavedFitBox'
import { FIT_FINDER_HASH } from '@consts/index'
import { useAuth } from '@api/accounts/auth/hooks/useAuth'
import { Order } from '../../types'
import { NoSavedFitContent } from '../my-fit/NoSavedFit'
import { getCustomerFitCondensed } from '@store/redux/selectors/customerFitSelectors'
import { useSelector } from 'react-redux'
import OrderItemImage from '../orders/shared/OrderItemImage'
import { getOrderImages } from '../orders/helpers/getOrderImages'
import { formatDate } from '@helpers/utils'
import { checkIsCompleteFit } from '@helpers/customer/fit'
import { useEffect, useState } from 'react'

function OverviewAccountDetailsLine({ addressLine }: { addressLine?: string }) {
  const addressLineTrimmed = addressLine?.trim()
  if (!addressLineTrimmed) {
    return null
  }
  return <p>{addressLineTrimmed}</p>
}

function getOrdersListImages(orders: Order[]) {
  if (orders?.length === 0) {
    return []
  }

  const orderImages = orders.flatMap(order =>
    getOrderImages(order.fulfillments, order.lineItems)
  )

  return orderImages
}

function AccountOverview() {
  const { userProfile } = useAuth()
  const customerFit = useSelector(getCustomerFitCondensed)
  const savedFit = customerFit?.fit
  const orderImages =
    userProfile?.orders && getOrdersListImages(userProfile.orders)
  const [isClient, setIsClient] = useState(false)
  const hasSavedFit = isClient && savedFit && checkIsCompleteFit(savedFit)

  useEffect(() => {
    setIsClient(true)
  }, [])

  return (
    <div className={styles.OverviewGrid}>
      <Column>
        <ColumnHeader
          title="Orders"
          linkText={orderImages?.length ? 'View' : 'Continue Shopping'}
          linkUrl={orderImages?.length ? null : '/collections/best-sellers'}
        />
        <ColumnContent>
          <p>Includes Order History, Tracking and Returns.</p>
          <div className={styles.OverviewGrid__orderHistory}>
            {orderImages?.length ? (
              orderImages.slice(0, 3).map((image, index) => (
                // eslint-disable-next-line @next/next/no-img-element
                <OrderItemImage image={image} key={index} />
              ))
            ) : (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src="/static/images/account/no-orders-image.jpg"
                alt="Order Item"
              />
            )}
          </div>
        </ColumnContent>
      </Column>
      <Column>
        <ColumnHeader
          title="Your Fit"
          linkText={hasSavedFit ? 'View' : 'Take our Fit Finder'}
          linkUrl={hasSavedFit ? null : `#${FIT_FINDER_HASH}`}
        />
        <ColumnContent>
          {hasSavedFit && customerFit ? (
            <>
              {customerFit.updatedAt && (
                <p>Last updated {formatDate(customerFit.updatedAt)}.</p>
              )}

              <div className={styles.OverviewGrid__myFit}>
                <SavedFitBoxContent savedFit={savedFit} />
              </div>
            </>
          ) : (
            <div className={styles.OverviewGrid__noSavedFit}>
              <NoSavedFitContent />
            </div>
          )}
        </ColumnContent>
      </Column>
      <Column classes={styles.OverviewGrid__finalColumn}>
        <ColumnHeader title="Account Details" linkText="View" linkUrl={null} />
        <ColumnContent>
          <p>Includes Personal Details and Address Book.</p>
          <br />

          {userProfile && (
            <address className={styles.OverviewGrid__accountDetails}>
              <OverviewAccountDetailsLine
                addressLine={`${
                  userProfile?.defaultAddress?.firstName ||
                  userProfile.firstName ||
                  ''
                } ${
                  userProfile?.defaultAddress?.lastName ||
                  userProfile.lastName ||
                  ''
                }`}
              />
              <OverviewAccountDetailsLine
                addressLine={
                  userProfile?.defaultAddress?.address1 ||
                  userProfile.addresses?.[0]?.address1
                }
              />
              <OverviewAccountDetailsLine
                addressLine={
                  userProfile?.defaultAddress?.address2 ||
                  userProfile.addresses?.[0]?.address2
                }
              />
              <OverviewAccountDetailsLine
                addressLine={
                  userProfile?.defaultAddress?.city ||
                  userProfile.addresses?.[0]?.city
                }
              />
              <OverviewAccountDetailsLine
                addressLine={
                  userProfile?.defaultAddress?.zip ||
                  userProfile.addresses?.[0]?.zip
                }
              />
              <OverviewAccountDetailsLine
                addressLine={userProfile.phoneNumber?.phoneNumber}
              />
              <OverviewAccountDetailsLine
                addressLine={userProfile.emailAddress?.emailAddress}
              />
            </address>
          )}
        </ColumnContent>
      </Column>
    </div>
  )
}
export default AccountOverview
